import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  RefreshCw, 
  Check,
  X,
  Wifi,
  WifiOff,
  Calendar,
  Users,
  ArrowRightLeft,
  DollarSign,
  Upload
} from 'lucide-react';

import { API } from '../../lib/api';

export default function AdminBeds24() {
  const { token } = useAuth();
  const [status, setStatus] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncingPrices, setSyncingPrices] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [debugData, setDebugData] = useState(null);
  const [showDebug, setShowDebug] = useState(false);
  
  // Form per sincronizzazione prezzi
  const [priceForm, setPriceForm] = useState({
    date_from: '',
    date_to: '',
    price: '',
    min_stay: 1
  });

  useEffect(() => {
    fetchStatus();
    fetchBookings();
  }, [token]);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/beds24/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
    } catch (error) {
      console.error('Error fetching Beds24 status:', error);
      setStatus({ connected: false, error: 'Errore di connessione' });
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/admin/beds24/bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data.bookings || []);
    } catch (error) {
      console.error('Error fetching Beds24 bookings:', error);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/admin/beds24/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const result = response.data;
      setLastSync(new Date().toLocaleString('it-IT'));
      
      if (result.errors && result.errors.length > 0) {
        toast.warning(`Sincronizzazione completata con ${result.errors.length} errori`);
      } else {
        toast.success(`✓ Sincronizzate ${result.imported} nuove, ${result.updated} aggiornate`);
      }
      
      fetchBookings();
    } catch (error) {
      toast.error('Errore durante la sincronizzazione');
    } finally {
      setSyncing(false);
    }
  };

  const handleForceRefresh = async () => {
    if (!window.confirm('Questo cancellerà tutte le prenotazioni Beds24 e le reimporterà. Continuare?')) {
      return;
    }
    
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/admin/beds24/force-refresh`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const result = response.data;
      toast.success(`✓ ${result.deleted} cancellate, ${result.imported} reimportate`);
      setLastSync(new Date().toLocaleString('it-IT'));
      fetchBookings();
    } catch (error) {
      toast.error('Errore durante il refresh');
    } finally {
      setSyncing(false);
    }
  };

  // Debug: mostra dati raw da Beds24
  const handleDebugRaw = async () => {
    try {
      setDebugData({ loading: true });
      const response = await axios.get(`${API}/admin/beds24/debug-raw`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDebugData(response.data);
      setShowDebug(true);
    } catch (error) {
      setDebugData({ error: error.response?.data?.detail || error.message });
      setShowDebug(true);
    }
  };

  const handleSyncPrices = async (e) => {
    e.preventDefault();
    if (!priceForm.date_from || !priceForm.date_to || !priceForm.price) {
      toast.error('Compila tutti i campi');
      return;
    }
    
    setSyncingPrices(true);
    try {
      const response = await axios.post(`${API}/admin/beds24/prices/sync`, {
        date_from: priceForm.date_from,
        date_to: priceForm.date_to,
        price: parseFloat(priceForm.price),
        min_stay: parseInt(priceForm.min_stay) || 1
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      setPriceForm({ date_from: '', date_to: '', price: '', min_stay: 1 });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore sincronizzazione prezzi');
    } finally {
      setSyncingPrices(false);
    }
  };

  const handleSyncAllPrices = async () => {
    setSyncingPrices(true);
    try {
      const response = await axios.post(`${API}/admin/beds24/sync-all-prices`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore sincronizzazione prezzi');
    } finally {
      setSyncingPrices(false);
    }
  };

  const getSourceBadge = (booking) => {
    const source = booking.apiSource || booking.channel || 'Direct';
    const colors = {
      'Airbnb': 'bg-[#FF5A5F] text-white',
      'Booking.com': 'bg-blue-600 text-white',
      'Direct': 'bg-gray-500 text-white'
    };
    return (
      <Badge className={colors[source] || 'bg-gray-500 text-white'}>
        {source}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short'
    });
  };

  if (loading) {
    return (
      <AdminLayout title="Beds24">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-[#C5A059]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Beds24 Channel Manager">
      <div className="space-y-6">

        {/* Status Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {status?.connected ? (
                <Wifi className="h-5 w-5 text-green-500" />
              ) : (
                <WifiOff className="h-5 w-5 text-red-500" />
              )}
              Stato Connessione
            </CardTitle>
          </CardHeader>
          <CardContent>
            {status?.connected ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-green-600">
                  <Check className="h-5 w-5" />
                  <span className="font-semibold">Connesso a Beds24</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Proprietà</p>
                    <p className="font-semibold">{status.property_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">ID</p>
                    <p className="font-mono">{status.property_id}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Città</p>
                    <p>{status.city}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Email</p>
                    <p className="truncate">{status.email}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-red-600">
                <X className="h-5 w-5" />
                <span>{status?.error || 'Non connesso'}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sync Card */}
        {status?.connected && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <ArrowRightLeft className="h-5 w-5 text-[#C5A059]" />
                    Sincronizzazione
                  </CardTitle>
                  <CardDescription>
                    Importa le prenotazioni da Airbnb, Booking.com e altri canali tramite Beds24
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={handleSync}
                    disabled={syncing}
                    className="bg-[#C5A059] hover:bg-[#B08A3E]"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                    {syncing ? 'Sincronizzazione...' : 'Sincronizza Ora'}
                  </Button>
                  <Button 
                    onClick={handleForceRefresh}
                    disabled={syncing}
                    variant="outline"
                    className="border-red-300 text-red-600 hover:bg-red-50"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                    Force Refresh
                  </Button>
                  <Button 
                    onClick={handleDebugRaw}
                    variant="outline"
                    className="border-purple-300 text-purple-600 hover:bg-purple-50"
                  >
                    🔍 Debug Raw
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {lastSync && (
                <p className="text-sm text-gray-500 mb-4">
                  Ultima sincronizzazione: {lastSync}
                </p>
              )}
              
              {/* Debug Panel */}
              {showDebug && debugData && (
                <div className="mb-4 p-4 bg-gray-900 text-green-400 rounded-lg font-mono text-xs overflow-auto max-h-96">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white font-bold">🔍 DEBUG: Dati Raw Beds24</span>
                    <button 
                      onClick={() => setShowDebug(false)}
                      className="text-red-400 hover:text-red-300"
                    >
                      ✕ Chiudi
                    </button>
                  </div>
                  {debugData.loading ? (
                    <p>Caricamento...</p>
                  ) : debugData.error ? (
                    <p className="text-red-400">Errore: {debugData.error}</p>
                  ) : (
                    <pre className="whitespace-pre-wrap">
                      {JSON.stringify(debugData, null, 2)}
                    </pre>
                  )}
                </div>
              )}
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                  <Check className="h-4 w-4" />
                  Sincronizzazione Automatica Attiva
                </h4>
                <p className="text-sm text-green-700">
                  Le prenotazioni da Airbnb e Booking.com vengono sincronizzate automaticamente tramite Beds24.
                  Usa il pulsante "Sincronizza Ora" per forzare un aggiornamento immediato.
                </p>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="font-semibold text-amber-800 mb-2">⚠️ Se i nomi non si aggiornano</h4>
                <p className="text-sm text-amber-700">
                  Usa il pulsante rosso "Force Refresh" per cancellare e reimportare tutte le prenotazioni Beds24.
                  Le prenotazioni manuali non verranno toccate.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Prices Sync Card */}
        {status?.connected && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-[#C5A059]" />
                Sincronizzazione Prezzi
              </CardTitle>
              <CardDescription>
                Invia i prezzi dal tuo pannello a Beds24, Airbnb e Booking.com
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Quick sync from local pricing */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-blue-800">Sincronizza Prezzi Configurati</h4>
                    <p className="text-sm text-blue-700">
                      Invia tutte le tariffe configurate nella sezione "Prezzi" a Beds24
                    </p>
                  </div>
                  <Button 
                    onClick={handleSyncAllPrices}
                    disabled={syncingPrices}
                    variant="outline"
                    className="border-blue-300 text-blue-700 hover:bg-blue-100"
                  >
                    <Upload className={`h-4 w-4 mr-2 ${syncingPrices ? 'animate-spin' : ''}`} />
                    Sincronizza Tutti
                  </Button>
                </div>
              </div>

              {/* Manual price form */}
              <div>
                <h4 className="font-semibold mb-3">Imposta Prezzo Manuale</h4>
                <form onSubmit={handleSyncPrices} className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <Label htmlFor="date_from">Data Inizio</Label>
                      <Input
                        id="date_from"
                        type="date"
                        value={priceForm.date_from}
                        onChange={(e) => setPriceForm({...priceForm, date_from: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="date_to">Data Fine</Label>
                      <Input
                        id="date_to"
                        type="date"
                        value={priceForm.date_to}
                        onChange={(e) => setPriceForm({...priceForm, date_to: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="price">Prezzo/Notte (€)</Label>
                      <Input
                        id="price"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="100.00"
                        value={priceForm.price}
                        onChange={(e) => setPriceForm({...priceForm, price: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="min_stay">Soggiorno Min.</Label>
                      <Input
                        id="min_stay"
                        type="number"
                        min="1"
                        value={priceForm.min_stay}
                        onChange={(e) => setPriceForm({...priceForm, min_stay: e.target.value})}
                      />
                    </div>
                  </div>
                  <Button 
                    type="submit"
                    disabled={syncingPrices}
                    className="bg-[#C5A059] hover:bg-[#B08A3E]"
                  >
                    <Upload className={`h-4 w-4 mr-2 ${syncingPrices ? 'animate-spin' : ''}`} />
                    Invia a Beds24
                  </Button>
                </form>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bookings Preview */}
        {status?.connected && bookings.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-[#C5A059]" />
                Prenotazioni da Beds24
                <Badge variant="secondary">{bookings.length}</Badge>
              </CardTitle>
              <CardDescription>
                Anteprima delle prenotazioni sincronizzate dai vari canali
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {bookings.slice(0, 10).map((booking, idx) => (
                  <div 
                    key={booking.id || idx}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      {getSourceBadge(booking)}
                      <div>
                        <p className="font-medium">
                          {booking.guestFirstName} {booking.guestLastName || ''}
                        </p>
                        <p className="text-sm text-gray-500">
                          Ref: {booking.apiReference || booking.id}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="text-right">
                        <p className="font-medium">
                          {formatDate(booking.arrival)} → {formatDate(booking.departure)}
                        </p>
                        <p className="text-gray-500">
                          <Users className="h-3 w-3 inline mr-1" />
                          {booking.numAdult || 1} adulti
                          {booking.numChild > 0 && `, ${booking.numChild} bambini`}
                        </p>
                      </div>
                      <Badge variant={booking.status === 'cancelled' ? 'destructive' : 'outline'}>
                        {booking.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
              
              {bookings.length > 10 && (
                <p className="text-center text-sm text-gray-500 mt-4">
                  E altre {bookings.length - 10} prenotazioni...
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 mb-2">Informazioni su Beds24</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Beds24 è il tuo Channel Manager centrale per gestire tutti i canali</li>
                <li>• Le prenotazioni da Airbnb, Booking.com e altri vengono sincronizzate automaticamente</li>
                <li>• Le modifiche fatte qui possono essere inviate a Beds24 (sincronizzazione bidirezionale)</li>
                <li>• Per configurare nuovi canali, accedi al pannello Beds24</li>
              </ul>
            </div>
          </CardContent>
        </Card>

      </div>
    </AdminLayout>
  );
}
